















import Vue from 'vue'
import Icon from './Icon.vue'

const DISMISSED_STORAGE_KEY = 'pubwatchPlacementDismissed'
const ADSENSE_WAIT_MS = 5000

function dismissedIds (): Record<string, boolean> {
  try {
    return JSON.parse(localStorage?.getItem(DISMISSED_STORAGE_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

function reviveScripts (root: HTMLElement) {
  Array.from(root.querySelectorAll('script')).forEach(old => {
    const next = document.createElement('script')
    Array.from(old.attributes).forEach(attr => next.setAttribute(attr.name, attr.value))
    next.textContent = old.textContent
    old.parentNode && old.parentNode.replaceChild(next, old)
  })
}

function ensureAdsenseLoader () {
  if (document.querySelector('script[src*="adsbygoogle.js"]')) {
    return
  }
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js'
  script.crossOrigin = 'anonymous'
  document.head.appendChild(script)
}

export default Vue.extend({
  components: { Icon },
  props: {
    slotKey: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      dismissed: false,
      adsensePending: false,
      adsenseFailed: false,
      adsenseObserver: null as MutationObserver | null,
      adsenseTimer: 0 as number
    }
  },
  computed: {
    banner (): any {
      return (this.$store.state.banners && this.$store.state.banners[this.slotKey]) || null
    },
    visibleBanner (): any {
      if (!this.banner || !this.banner.html || this.dismissed || this.adsenseFailed) {
        return null
      }
      return this.banner
    },
    bannerClasses (): string[] {
      const classes = ['placement-banner--' + this.slotKey]
      if (this.visibleBanner && (this.visibleBanner.kind === 'adsense' || this.visibleBanner.kind === 'html')) {
        classes.push('placement-banner--network')
      }
      if (this.visibleBanner && this.visibleBanner.kind === 'adsense') {
        classes.push('placement-banner--adsense')
      }
      if (this.adsensePending) {
        classes.push('placement-banner--pending')
      }
      return classes
    }
  },
  watch: {
    banner: {
      immediate: true,
      handler (banner: any) {
        this.adsenseFailed = false
        this.adsensePending = Boolean(banner && banner.kind === 'adsense')
        if (!banner || !banner.closable) {
          this.dismissed = false
          return
        }
        this.dismissed = Boolean(dismissedIds()[this.storageKey(banner)])
      }
    },
    visibleBanner: {
      immediate: true,
      handler () {
        this.$nextTick(() => this.activateEmbeddedScripts())
      }
    }
  },
  beforeDestroy () {
    this.stopAdsenseWatch()
  },
  methods: {
    storageKey (banner: any) {
      return (this.slotKey || 'slot') + ':' + String(banner.id)
    },
    stopAdsenseWatch () {
      if (this.adsenseObserver) {
        this.adsenseObserver.disconnect()
        this.adsenseObserver = null
      }
      if (this.adsenseTimer) {
        window.clearTimeout(this.adsenseTimer)
        this.adsenseTimer = 0
      }
    },
    activateEmbeddedScripts () {
      const root = this.$el && (this.$el as HTMLElement).querySelector('.placement-banner__content')
      if (!root) {
        return
      }
      reviveScripts(root as HTMLElement)
      if (this.visibleBanner && this.visibleBanner.kind === 'adsense') {
        ensureAdsenseLoader()
        this.watchAdsenseFill(root as HTMLElement)
      } else {
        this.adsensePending = false
        this.stopAdsenseWatch()
      }
    },
    watchAdsenseFill (root: HTMLElement) {
      this.stopAdsenseWatch()
      const ins = root.querySelector('ins.adsbygoogle') as HTMLElement | null
      if (!ins) {
        this.adsensePending = false
        return
      }
      this.adsensePending = true
      const inspect = () => {
        const status = ins.getAttribute('data-ad-status')
        if (status === 'unfilled') {
          this.adsenseFailed = true
          this.adsensePending = false
          this.stopAdsenseWatch()
          return true
        }
        if (status === 'filled') {
          this.adsensePending = false
          this.stopAdsenseWatch()
          return true
        }
        return false
      }
      if (inspect()) {
        return
      }
      if (window.MutationObserver) {
        this.adsenseObserver = new MutationObserver(inspect)
        this.adsenseObserver.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })
      }
      this.adsenseTimer = window.setTimeout(() => {
        const filled = ins.getAttribute('data-ad-status') === 'filled' || Boolean(ins.querySelector('iframe'))
        this.adsensePending = false
        if (!filled) {
          this.adsenseFailed = true
        }
        this.stopAdsenseWatch()
      }, ADSENSE_WAIT_MS)
    },
    dismiss () {
      if (!this.banner) {
        return
      }
      const records = dismissedIds()
      records[this.storageKey(this.banner)] = true
      localStorage?.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(records))
      this.dismissed = true
    }
  }
})
